/* eslint-disable no-console */
import { ApiModule, getProxyRoute, httpClient } from './api';

/** @import { AxiosResponse } from 'axios' */
/**
 * @import {
 * AlertaNotificacion,
 * Almacen,
 * Articulo,
 * BusquedaAlertaNotificacion,
 * CambiarEstadoListines,
 * Centro,
 * Cliente,
 * ConfeccionHabitualPedidos,
 * ConfiguracionDistribucionAPP,
 * Corredor,
 * Empresa,
 * Entorno,
 * EntornoDeTrabajo,
 * EstadoMercancia,
 * GrupoEmpresarial,
 * Imagen,
 * LineaPedidoVenta,
 * LineaProduccionMotivoParada,
 * LineaProduccionPeriodoInactividad,
 * ListinVenta,
 * Modulo,
 * MovimientoArticRetornables,
 * Operacion,
 * Pais,
 * ParametroConfiguracion,
 * Partida,
 * PedidoArticuloRetornable,
 * PedidoArticuloRetornableLinea,
 * PrioridadProduccionPorTipoEntidad,
 * RecuentoExistencia,
 * Respuesta,
 * RespuestaValidacion,
 * Sujeto,
 * TipoAplicacionCliente,
 * TipoCliente,
 * TrabajadorElementoProduccion,
 * UnidadContenedora,
 * Venta,
 * } from '@erpagroapp/types'
 */

/**
 * Calls the API to retrieve line workers for a given production line and date.
 * @param {number} productionLineId - The ID of the production line.
 * @param {string} date - The date.
 * @returns {Promise<AxiosResponse<TrabajadorElementoProduccion[]>>}
 */
export function callLineWorkersToDate(productionLineId, date) {
  const params = {
    idLineaProduccion: productionLineId,
    fechaMomento: date,
  };
  return httpClient().get(`${ApiModule.Production}/ObtenerTrabajadorElementoProduccionXLineaDeProduccionAFecha`, { params });
}

/**
 * Calls the API to retrieve profile photos for a list of subjects.
 * @param {Sujeto[]} subjectsList - The list of subjects for which to retrieve profile photos.
 * @returns {Promise<AxiosResponse<Sujeto[]>>}
 */
export function callGetPhotosProfileSubjects(subjectsList) {
  return httpClient().post(`${ApiModule.Presence}/ObtenerFotosPerfilSujetos`, subjectsList);
}

/**
 * Calls the ERP stored procedure with the provided parameters and mapping.
 *
 * @param {string} nameSP - The name of the stored procedure to execute.
 * @param {Record<string, any>} params - The parameters to pass to the stored procedure.
 * @param {string} mappingEntity - The mapping entity for the response.
 * @returns {Promise<AxiosResponse<any>>}
 */
export function callExecuteSPERP(nameSP, params, mappingEntity) {
  const data = {
    Parametros: params,
    NombreSP: nameSP,
    TipoRespuesta: 2, // Objeto de ERP serializado en JSON
    EntidadMapear: mappingEntity,
  };
  return httpClient().post('/api/EjecutarSPERP', data);
}

/**
 * Calls the specified stored procedure to fetch countries.
 *
 * @param {object} [params] - The parameters object (optional).
 * @param {string} [params.filter] - The filter to apply (e.g., 'q:').
 * @returns {Promise<AxiosResponse<Pais[]>>}
 */
export function callFetchCountries({ filter = '' } = {}) {
  return callExecuteSPERP('APPERPAGRO_Paises', [
    { NombreParametro: 'filtro', Valor: filter },
  ], 'Comun.PaisJSON');
}

/**
 * Calls the specified stored procedure to fetch clients belonging to the specified enterprise group(s).
 *
 * @param {object} [params] - The parameters object (optional).
 * @param {string} [params.filter] - The filter to apply (e.g., 'q:').
 * @param {number[]} [params.enterpriseGroupIds] - An array of enterprise group IDs.
 * @param {number} [params.companyId] - The ID of the company.
 * @param {number} [params.sellerSubjectId] - The ID of the seller subject (`Sujeto` entity).
 * @returns {Promise<AxiosResponse<Cliente[]>>}
 */
export function callFetchClientsXEnterpriseGroup({
  filter = '',
  enterpriseGroupIds = [],
  companyId = 0,
  sellerSubjectId = 0,
} = {}) {
  return callExecuteSPERP('APPERPAGRO_ClientesXIdGrupoEmpresarial', [
    { NombreParametro: 'filtro', Valor: filter },
    { NombreParametro: 'idsGrupoEmpresarial', Valor: enterpriseGroupIds.join(',') },
    { NombreParametro: 'idEmpresa', Valor: companyId },
    { NombreParametro: 'fechaComprobacionBloqueos', Valor: new Date() },
    { NombreParametro: 'idSujetoVendedor', Valor: sellerSubjectId },
  ], 'Comun.ClienteJSON');
}

/**
 * Fetches warehouses for enterprise groups and locations.
 *
 * @param {object} [params] - The parameters object (optional).
 * @param {string} [params.filter] - The filter string (e.g., 'q:').
 * @param {number[]} [params.enterpriseGroupIds] - An array of enterprise group IDs.
 * @param {number[]} [params.locationIds] - An array of location IDs.
 * @returns {Promise<AxiosResponse<Almacen[]>>}
 */
export function callFetchWarehousesForEnterpriseGroupsAndLocations({
  filter = '',
  enterpriseGroupIds = [],
  locationIds = [],
} = {}) {
  return callExecuteSPERP('APPERPAGRO_AlmacenesXIdGrupoEmpresarialXUbicacion', [
    { NombreParametro: 'filtro', Valor: filter },
    { NombreParametro: 'idsGrupoEmpresarial', Valor: enterpriseGroupIds.join(',') },
    { NombreParametro: 'idsUbicacion', Valor: locationIds.join(',') },
  ], 'AlmacenJSON');
}

/**
 * Calls the API endpoint to fetch active production line stops (`LineaProduccionPeriodoInactividad` entity).
 *
 * @param {object} params - The parameters for the API call.
 * @param {number[]} [params.productionLineIds] - The ID of the production line.
 * @param {string | null} [params.startDate] - The start date in the ISO format.
 * @param {string | null} [params.endDate] - The end date in the ISO format.
 * @returns {Promise<AxiosResponse<LineaProduccionPeriodoInactividad[]>>}
 */
export function callFetchActiveProductionLineStops({
  productionLineIds = [],
  startDate = null, endDate = null,
} = {}) {
  return callExecuteSPERP('APPERPAGRO_LineaProduccionPeriodosInactividadActivos', [
    {
      NombreParametro: 'idsLineasProduccion',
      Valor: productionLineIds.join(','),
    },
    {
      NombreParametro: 'fechaInicio',
      Valor: startDate,
    },
    {
      NombreParametro: 'fechaFin',
      Valor: endDate,
    },
  ], 'Produccion.LineaProduccionPeriodoInactividadJSON');
}

/**
 * Calls the API endpoint to fetch active production line stops (`LineaProduccionPeriodoInactividad` entity).
 *
 * @param {number} productionLineId - The ID of the production line.
 * @param {string} workingDate - The working date for which to fetch active production line stops.
 * @returns {Promise<AxiosResponse<LineaProduccionPeriodoInactividad[]>>}
 */
export function callFetchActiveProductionLineStopsForWorkingDate(productionLineId, workingDate) {
  return callExecuteSPERP('APPERPAGRO_LineaProduccionPeriodosInactividadActivosXJornada', [
    {
      NombreParametro: 'idLineaProduccion',
      Valor: productionLineId,
    },
    {
      NombreParametro: 'fechaJornada',
      Valor: workingDate,
    },
  ], 'Produccion.LineaProduccionPeriodoInactividadJSON');
}

/**
 * Calls the API endpoint to fetch production line stops reasons (`LineaProduccionMotivoParada` entity).
 *
 * @param {string} [filter] - The filter to apply to the API call.
 * @returns {Promise<AxiosResponse<LineaProduccionMotivoParada[]>>}
 */
export function callFetchProductionLineStopsReasons(filter = '') {
  return callExecuteSPERP('APPERPAGRO_LineaProduccionMotivoParada', [
    {
      NombreParametro: 'filtro',
      Valor: filter,
    },
  ], 'Produccion.LineaProduccionMotivoParadaJSON');
}

/**
 * Saves a production line stop. If it already exists, it updates it.
 *
 * @param {PartialDeep<LineaProduccionPeriodoInactividad>} stop - The stop object to edit.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callSaveProductionLineStop(stop) {
  return httpClient().post(`${ApiModule.Production}/SaveProductionLineStop`, stop);
}

/**
 * Calls the API endpoint to finish a production line stop (`LineaProduccionPeriodoInactividad` entity).
 *
 * @param {number} stopId - The ID of the production line stop to finish (`LineaProduccionPeriodoInactividad` entity).
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callFinishProductionLineStop(stopId) {
  const params = {
    stopId,
  };
  return httpClient().post(`${ApiModule.Production}/FinishProductionLineStop`, null, { params });
}

/**
 * Fetches client types.
 *
 * @param {object} [params] - The params object (optional).
 * @param {string} [params.filter] - Filter string (e.g. `q:`).
 * @param {number[]} [params.enterpriseGroupIds] - IDs of enterprise groups (optional).
 * @returns {Promise<AxiosResponse<TipoCliente[]>>}
 */
export function callFetchClientTypes({
  filter = '',
  enterpriseGroupIds = [],
} = {}) {
  return callExecuteSPERP('APPERPAGRO_TiposCliente', [
    { NombreParametro: 'filtro', Valor: filter },
    { NombreParametro: 'idsGrupoEmpresarial', Valor: enterpriseGroupIds.join(',') },
  ], 'Comun.TipoClienteJSON');
}

/**
 * Fetches production priority entities.
 *
 * @param {object} [params] - The params object (optional).
 * @param {string} [params.filter] - Filter string (`id:` or `tipo:`).
 * @returns {Promise<AxiosResponse<PrioridadProduccionPorTipoEntidad[]>>}
 */
export function callFetchProductionPriorityEntity({ filter = '' } = {}) {
  return callExecuteSPERP('APPERPAGRO_PrioridadesProduccionPorTipoEntidad', [
    { NombreParametro: 'filtro', Valor: filter },
  ], 'Produccion.PrioridadProduccionPorTipoEntidadJSON');
}

/**
 * Saves a production priority entity (`PrioridadProduccionPorTipoEntidad`). If it already exists, it updates it.
 *
 * @param {Partial<PrioridadProduccionPorTipoEntidad>} priorityEntity - The priority entity object to save.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callSaveProductionPriorityEntity(priorityEntity) {
  return httpClient().post(`${ApiModule.Production}/SaveProductionPriorityEntity`, priorityEntity);
}

/**
 * Creates an article serial number.
 * @param {Partial<UnidadContenedora>} container - The data for creating the article serial number.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callCreateArticleSerialNumber(container) {
  return httpClient().post(`${ApiModule.Common}/CreateArticleSerialNumber`, container);
}

/**
 * Calls the API endpoint to check if a new version is available.
 * @param {string} appName - The application name.
 * @param {string} appVersion - The current application version.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callCheckAppUpdate(appName, appVersion) {
  const params = {
    nombreAPP: appName,
    versionInstalada: appVersion,
  };
  return httpClient().get(`${ApiModule.Apps}/ComprobarNuevaActualicionApp`, { params });
}

/**
 * Fetches user processes accessibility.
 * @param {object} params - The params object.
 * @param {TipoAplicacionCliente} params.applicationType - The application type.
 * @param {string | null} [params.language] - The user's language.
 * @returns {Promise<AxiosResponse<Modulo[]>>}
 */
export function callProcessesAccessibility({
  applicationType,
  language = null,
}) {
  const params = {
    tipoAplicacionCliente: applicationType,
    idioma: language,
  };
  return httpClient().post(`${ApiModule.Security}/ObtenerProcesosAccesibles`, null, { params });
}

/**
 * Moves a reserved pallet from one expedition to another.
 * @param {number} serialNumberId - The ID of the serial number (`ArticuloNumeroSerie` entity).
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callMoveReservedPalletDifferentExpedition(serialNumberId) {
  const params = {
    serialNumberId,
  };
  return httpClient().post(`${ApiModule.Commercialization}/Expediciones/MoveReservedPalletDifferentExpedition`, null, { params });
}

/**
 * Retrieves the configuration parameters of the application.
 * @param {PartialDeep<ParametroConfiguracion>[]} parameters
 * An array of configuration parameters to retrieve.
 * @returns {Promise<AxiosResponse<ParametroConfiguracion[]>>}
 */
export function callGetConfigurationParameters(parameters) {
  return httpClient().post(`${ApiModule.Apps}/GetConfigurationParameters`, parameters);
}

/**
 * Validates the internal credentials.
 * @param {object} params - The params object.
 * @param {string} params.username - The user name.
 * @param {string} params.password - The password.
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callValidateInternalCredentials({
  username,
  password,
}) {
  const params = { username, password };
  return httpClient().post(`${ApiModule.Apps}/ValidateInternalCredentials`, null, { params });
}

/**
 * Calls the API endpoint to get alerts.
 * @param {BusquedaAlertaNotificacion[]} alertFilters
 * @returns {Promise<AxiosResponse<AlertaNotificacion[]>>}
 */
export function callGetAlertsERP(alertFilters) {
  return httpClient().post(`${ApiModule.Alerts}/ObtenerAlertasNotificacion`, alertFilters);
}

/**
 * Calls the API endpoint to change the status of one or more alerts.
 * @param {AlertaNotificacion[]} alertsList
 * @returns {Promise<AxiosResponse<RespuestaValidacion>>}
 */
export function callChangeAlertStatus(alertsList) {
  return httpClient().post(`${ApiModule.Alerts}/CambiarEstadoAlertaNotificacion`, alertsList);
}

/**
 * Gets the user's work environment.
 * @returns {Promise<AxiosResponse<Entorno>>}
 */
export function callGetUserEnvironment() {
  return httpClient().get(`${ApiModule.Apps}/ObtenerEntornoUsuario`);
}

/**
 * Sets the user's work environment.
 * @param {PartialDeep<EntornoDeTrabajo>} workEnvironmentJSON
 * @returns {Promise<AxiosResponse<Respuesta>>}
 */
export function callSetWorkEnvironment(workEnvironmentJSON) {
  return httpClient().post(`${ApiModule.Apps}/SetUserWorkEnvironment`, workEnvironmentJSON);
}

/**
 * Gets the info about the database.
 * @param {string} hostApiERP
 * @returns {Promise<AxiosResponse<Record<string, any>>>}
 */
export function callInfoApp(hostApiERP) {
  const headers = {
    username: 'Admin',
    password: 'Admin',
  };
  return httpClient({ baseURL: undefined }).get(`${getProxyRoute(hostApiERP)}`.concat(`${ApiModule.Apps}/InfoApp`), { headers });
}

/**
 * Fetches accessible operations for user's work environment.
 * @param {number | null} userId - The ID of the user.
 * @returns {Promise<AxiosResponse<Operacion[]>>}
 */
export function callFetchAvailableWorkEnvironmentOperations(userId) {
  return callExecuteSPERP('APPERPAGRO_ObtenerOperacionesEntornoTrabajoXUsuario', [
    {
      NombreParametro: 'IdUsuario',
      Valor: userId,
    },
  ], 'OperacionJSON');
}

/**
 * Fetches accessible enterprises groups.
 * @param {object} params - The parameters.
 * @param {number | null} params.accessibleEnvironmentId - The ID of the accesible environment.
 * @param {string} [params.filter] - The filtering value. (Optional)
 * @returns {Promise<AxiosResponse<GrupoEmpresarial[]>>}
 */
export function callFetchAccessibleEnterpriseGroups({
  accessibleEnvironmentId,
  filter = '',
}) {
  return callExecuteSPERP('APPERPAGRO_GruposEmpresarialesXEntornoAccesible', [
    {
      NombreParametro: 'IdEntornoAccesible',
      Valor: accessibleEnvironmentId,
    },
    {
      NombreParametro: 'Filtro',
      Valor: filter,
    },
  ], 'GrupoEmpresarialJSON');
}

/**
 * Fetches accessible companies.
 * @param {object} params - The parameters.
 * @param {number | null} params.accessibleEnvironmentId - The ID of the accesible environment.
 * @param {number | null} params.enterpriseGroupId - The ID of the enterprise group.
 * @param {string} [params.filter] - The filtering value. (Optional)
 * @returns {Promise<AxiosResponse<Empresa[]>>}
 */
export function callFetchAccessibleCompanies({
  accessibleEnvironmentId,
  enterpriseGroupId,
  filter = '',
}) {
  return callExecuteSPERP('APPERPAGRO_EmpresasXEntornoAccesible', [
    {
      NombreParametro: 'IdEntornoAccesible',
      Valor: accessibleEnvironmentId,
    },
    {
      NombreParametro: 'IdGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
    {
      NombreParametro: 'Filtro',
      Valor: filter,
    },
  ], 'EmpresaJSON');
}

/**
 * Fetches accessible centers.
 * @param {object} params - The parameters.
 * @param {number | null} params.accessibleEnvironmentId - The ID of the accesible environment.
 * @param {number | null} params.enterpriseGroupId - The ID of the enterprise group.
 * @param {number | null} params.companyId - The ID of the company.
 * @param {string} [params.filter] - The filtering value. (Optional)
 * @returns {Promise<AxiosResponse<Centro[]>>}
 */
export function callFetchAccessibleCenters({
  accessibleEnvironmentId,
  enterpriseGroupId,
  companyId,
  filter = '',
}) {
  return callExecuteSPERP('APPERPAGRO_CentrosXEntornoAccesible', [
    {
      NombreParametro: 'IdEntornoAccesible',
      Valor: accessibleEnvironmentId,
    },
    {
      NombreParametro: 'IdGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
    {
      NombreParametro: 'IdEmpresa',
      Valor: companyId,
    },
    {
      NombreParametro: 'Filtro',
      Valor: filter,
    },
  ], 'CentroJSON');
}

/**
 * Fetches accessible warehouses.
 * @param {object} params - The parameters object.
 * @param {number | null} params.enterpriseGroupId - The ID of the enterprise group.
 * @param {number | null} params.userId - The ID of the user.
 * @param {string} [params.filter] - The filtering value. (Optional)
 * @returns {Promise<AxiosResponse<Almacen[]>>}
 */
export function callFetchAccessibleWarehouses({
  enterpriseGroupId,
  userId,
  filter = '',
}) {
  return callExecuteSPERP('APPERPAGRO_AlmacenesXGrupoEmpresarial', [
    {
      NombreParametro: 'IdGrupoEmpresarial',
      Valor: enterpriseGroupId,
    },
    {
      NombreParametro: 'IdUsuario',
      Valor: userId,
    },
    {
      NombreParametro: 'Filtro',
      Valor: filter,
    },
  ], 'AlmacenJSON');
}

/**
 * Fetches common packaging sales orders.
 * @param {number} clientId
 * @param {number} companyId
 * @returns {Promise<AxiosResponse<ConfeccionHabitualPedidos[]>>}
 */
export function callFetchCommonPackagingSalesOrders(clientId, companyId) {
  return callExecuteSPERP('APPERPAGRO_ConfeccionesHabitualesPedidosVenta', [
    {
      NombreParametro: 'id_Cliente',
      Valor: clientId,
    },
    {
      NombreParametro: 'idEmpresa',
      Valor: companyId,
    },
    {
      NombreParametro: 'fechaComprobacionBloqueos',
      Valor: new Date(),
    },
  ], 'ConfeccionHabitualPedidosJSON');
}

/* 'http://localhost:8080' */
export default {
  /* MODULE LOGIN - COMMON */

  callAuthenticate(username, password) {
    return httpClient().post(`${ApiModule.Login}/authenticate`, null, { headers: { username, password } });
  },

  callLoginNFC(codigo) {
    //
    const params = {
      codigo,
    };
    return new Promise((resolve, reject) => {
      httpClient().post(`${ApiModule.Login}/LoginNFC`, null, {
        params,
      })
        .then((response) => {
          const objJson = response.data;

          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        })
        .then(() => {
          // always executed
        });
    });
  },

  callResetProcess(serviceName) {
    const params = {
      serviceName,
    };
    return httpClient().post(`${ApiModule.Apps}/ResetProcess`, null, { params });
  },

  /* MODULE PRODUCTION */

  callAccessDeviceList() {
    return httpClient().get(`${ApiModule.Production}/ObtenerDispositivosAcceso`);
  },

  callProductionActivitySearchCode(searchText) {
    const params = {
      codigo: searchText,
    };
    return httpClient().get(`${ApiModule.Production}/ObtenerActividadesProduccionPorCodigo`, { params });
  },

  callProductionActivityList() {
    return httpClient().get(`${ApiModule.Production}/ObtenerActividadesProduccion`);
  },

  callProductionActivityListAuthorized() {
    return httpClient().get(`${ApiModule.Production}/ObtenerActividadesProduccionAutorizadas`);
  },

  callProductionActivityListXLineAuthorized(idLine) {
    const params = {
      idLineaProduccion: idLine,
    };
    return httpClient().get(`${ApiModule.Production}/ObtenerActividadesProduccionAutorizadasXLineaProduccion`, { params });
  },

  callChangeActivityProductionDevice(lstDispositives) {
    return httpClient().post(`${ApiModule.Production}/ModificarActividadProduccionDispositivosDeAcceso`, lstDispositives);
  },

  callProductionLineSearchCode(searchText) {
    const params = {
      codigo: searchText,
    };
    return httpClient().get(`${ApiModule.Production}/ObtenerLineasProduccionPorCodigo`, { params });
  },

  callProductionLineList() {
    return httpClient().get(`${ApiModule.Production}/ObtenerLineasProduccion`);
  },

  callProductionLineListAuthorized(userCode) {
    const params = {
      codigoUsuario: userCode,
    };

    return httpClient().get(`${ApiModule.Production}/ObtenerLineasDeProduccionAutorizadas`, { params });
  },

  callGetProductionActivitiesXProductionLineToDate(idLine, dateOfconsult) {
    const params = {
      idLineaProduccion: idLine,
      fechaMomento: dateOfconsult,
    };
    return httpClient().get(`${ApiModule.Production}/ObtenerActividadesProduccionXLineaDeProduccionAFecha`, { params });
  },

  callGetWorkersOutputLine(idLine) {
    const params = {
      idLineaProduccion: idLine,
    };
    return httpClient().get(`${ApiModule.Production}/ObtenerTrabajadoresFueraDeLinea`, { params });
  },

  callGetRequestWorkersOutputLine(idLine) {
    const params = {
      idLineaProduccion: idLine,
    };
    return httpClient().get(`${ApiModule.Production}/ObtenerTrabajadoresEnColaParaSalidaDeLinea`, { params });
  },

  callRequestLineOutAuthorization(objLine, lstWorkers, moment) {
    const params = {
      idLineaProduccion: objLine.Id,
      fechaMomento: moment,
    };
    return httpClient().post(`${ApiModule.Production}/PeticionSalidaDeTrabajadorDeLineaParaAseos`, lstWorkers, { params });
  },

  callSavePetitionAreaAuthorization(lstPetition) {
    return httpClient().post(`${ApiModule.Production}/TrabajadorPeticionAutorizacionZona`, lstPetition);
  },

  callAuthorizeOutputWorker(idLine, idWorker/* , dateTime */) {
    const params = {
      idLineaProduccion: idLine,
      idTrabajador: idWorker,
    };
    const data = JSON.stringify(null);
    return httpClient().post(`${ApiModule.Production}/AutorizarSalidaDeTrabajadorDeLineaParaAseos`, data, { params });
  },

  callFinalizeOutputWorker(idLine, idWorker, dateTime) {
    const params = {
      idLineaProduccion: idLine,
      idTrabajador: idWorker,
      fechaMomento: dateTime,
    };
    return httpClient().post(`${ApiModule.Production}/RevocarSalidaDeTrabajadorDeLineaParaAseos`, null, { params });
  },

  callLinesListXActivity(idActivity) {
    const params = {
      idActividadProduccion: idActivity,
    };

    return httpClient().get(`${ApiModule.Production}/ObtenerLineasProduccionXActividad`, { params });
  },

  callPartOfProductionText(searchText) {
    const params = {
      cadenaBusqueda: searchText,
    };
    return httpClient().get(`${ApiModule.Production}/ParteProduccionBuqueda`, { params });
  },

  callPartOfProduction() {
    return httpClient().get(`${ApiModule.Production}/ParteProduccionLeerTodos`);
  },

  callActiveWorkerProductionPart(idWorker) {
    const params = {
      idTrabajador: idWorker,
    };
    return httpClient().get(`${ApiModule.Production}/ParteProduccionXTrabajadorActivo`, { params });
  },

  callInitWorkerInterventionInProductionPart(IdWorker, IdPart, IdLine) {
    const params = {
      idTrabajador: IdWorker,
      idParteProduccion: IdPart,
      idLineaProduccion: IdLine,
    };
    return httpClient().post(`${ApiModule.Production}/IntervencionTrabajadorXParteProduccionIniciar`, null, { params });
  },

  callFinishWorkerInterventionInProductionPart(idPPRT) {
    const params = {
      idParteProduccionRealTrabajador: idPPRT,
    };

    return httpClient().post(`${ApiModule.Production}/IntervencionTrabajadorXParteProduccionFinalizar`, null, { params });
  },

  callProductionLineArticleUsed(idLine, dateTime) {
    const params = {
      idLineaProduccion: idLine,
      fechaMomento: dateTime,
    };

    return httpClient().get(`${ApiModule.Production}/LineaProduccionArticuloUtilizadoXFechaXLinea`, { params });
  },

  callPartidaCodeSearch(searchText) {
    const params = {
      cadenaSerieNumero: searchText,
    };
    return httpClient().get(`${ApiModule.Production}/ObtenerPartidaEntradaPorSerieNumero`, { params });
  },

  callPartidasInList() {
    return httpClient().get(`${ApiModule.Production}/PartidasEntrada`);
  },

  callArticleCodeSearch(searchText) {
    const params = {
      codigo: searchText,
    };
    return httpClient().get(`${ApiModule.Production}/ObtenerArticuloPorCodigo`, { params });
  },

  callArticlesRawMaterialList(searchText) {
    const params = {
      filtroBusqueda: searchText,
    };
    return httpClient().get(`${ApiModule.Production}/ArticulosMateriaPrima`, { params });
  },

  callCreateNewProductionLineArticleUsed(lstObjNew) {
    return httpClient().post(`${ApiModule.Production}/LineaProduccionArticuloUtilizado`, lstObjNew);
  },

  callRemoveProductionLineArticleUsed(lstObjRemoved) {
    return httpClient().delete(`${ApiModule.Production}/LineaProduccionArticuloUtilizado`, { data: lstObjRemoved });
  },

  callRecalculateCurrentAndPreviousDays(idLine, dateRecalculate) {
    const params = {
      idLineaProduccion: idLine,
      fechaJornada: dateRecalculate,
    };
    return httpClient().get(`${ApiModule.Production}/RecalcularProduccionTrabajadores`, { params });
  },

  callExecuteAllocationOfTheElementsManufactured(manufacturedItemsList) {
    return httpClient().post(`${ApiModule.Production}/IndividualProduction/ExecuteAllocationOfTheElementsManufactured`, manufacturedItemsList);
  },

  callNewElaboratedArticle(manufacturedItemsList) {
    return httpClient().post(`${ApiModule.Production}/IndividualProduction/ElaboratedItem`, manufacturedItemsList);
  },

  callRemoveElementsAssociationManufacturedFromAPallet(lstObjRemoved) {
    return httpClient().delete(`${ApiModule.Production}/IndividualProduction/RemoveElementsAssociationManufacturedFromAPallet`, { data: lstObjRemoved });
  },

  callRemoveElaboratedArticle(lstObjRemoved) {
    return httpClient().delete(`${ApiModule.Production}/IndividualProduction/RemoveElaboratedArticle`, { data: lstObjRemoved });
  },

  callAutomaticGenerationOfProductionRecords(productionLine) {
    return httpClient().post(`${ApiModule.Production}/IndividualProduction/AutomaticGenerationOfProductionRecords`, productionLine);
  },

  /* MODULE PRESENCE */
  callMarkingIncidents(idLineaProduccion, fechaJornada) {
    const params = {
      idLineaProduccion,
      fechaJornada,
    };
    return httpClient().get(`${ApiModule.Presence}/ConsultarIncidenciasMarcajes`, { params });
  },

  callWorkerSearchText(searchText) {
    const params = {
      textoBusqueda: searchText,
    };
    return httpClient().get(`${ApiModule.Presence}/BusquedaTrabajador`, { params });
  },

  callWorkerList() {
    return httpClient().get(`${ApiModule.Presence}/ObtenerTrabajadoresPresencia`);
  },

  callWorkerRecords(idWorker, currentDayOnly) {
    const params = {
      idTrabajador: idWorker,
      soloJornadaActual: currentDayOnly,
    };
    return httpClient().get(`${ApiModule.Presence}/ObtenerMarcajesTrabajador`, { params });
  },

  callWorkerRecordsXDate(idWorker, consultationDate) {
    const params = {
      idTrabajador: idWorker,
      fechaConsulta: consultationDate,
    };
    return httpClient().get(`${ApiModule.Presence}/ObtenerMarcajesTrabajadorXJornada`, { params });
  },

  callRemoveWorkerRecord(idWorker, idRecord, idActivity) {
    const params = {
      idTrabajador: idWorker,
      idMarcaje: idRecord,
      idActividadProduccion: idActivity,
    };
    return httpClient().delete(`${ApiModule.Presence}/EliminarMarcajeTrabajador`, { params });
  },

  CallObtenerLogoCliente() {
    return httpClient().get(`${ApiModule.Apps}/ObtenerLogoCliente`);
  },

  callRecordsByActivityGroup(idWorker, dateOfQuery) {
    const params = {
      idTrabajador: idWorker,
      fechaJornada: dateOfQuery,
    };
    return httpClient().get(`${ApiModule.Presence}/ObtenerResumenMarcajesTrabajador`, { params });
  },

  callMakeNewRecordsWorkers(lstObjMarcajes) {
    return httpClient().post(`${ApiModule.Presence}/CrearMarcajesTrabajadores`, lstObjMarcajes);
  },

  callGetWorkersTimesActivity(idActivity, dateOfQuery, getExcessedTimeWorkersOnly) {
    const params = {
      idActividadProduccion: idActivity,
      fechaJornada: dateOfQuery,
      obtenerSoloExedidos: getExcessedTimeWorkersOnly,
    };
    return httpClient().get(`${ApiModule.Presence}/ObtenerTrabajadoresTiemposActividad`, { params });
  },

  callGetWorkersWithRecordsBetweenDates(idActivity, dateStartOfQuery, dateEndOfQuery) {
    const params = {
      idActividadProduccion: idActivity,
      fechaDesde: dateStartOfQuery,
      fechaHasta: dateEndOfQuery,
    };
    return httpClient().get(`${ApiModule.Presence}/ObtenerTrabajadoresConMarcajesEntreFechas`, { params });
  },

  /* PERSONALIZACIONES APP */
  callObtenerOperacionesPersonalizadas() {
    return httpClient().get(`${ApiModule.Apps}/personalizaciones/ObtenerOperacionesPersonalizadas`);
  },

  callObtenerOperacionesPersonalizadasEntidadMultiple(consultaPersonalizacionApp) {
    return httpClient().post(`${ApiModule.Apps}/personalizaciones/ObtenerOperacionesPersonalizadasEntidadMultiple`, consultaPersonalizacionApp);
  },

  callObtenerOperacionesPersonalizadasEntidad(consultaPersonalizacionApp) {
    return httpClient().post(`${ApiModule.Apps}/personalizaciones/ObtenerOperacionesPersonalizadasEntidad`, consultaPersonalizacionApp);
  },

  callEjecurarAccionPersonalizada(accionEjecucion, lstParametros) {
    const params = {
      parametrosJSON: lstParametros,
    };
    return httpClient().post(`${ApiModule.Apps}/personalizaciones/EjecutarAccionPersonalizada`, accionEjecucion, { params });
  },

  /* MODULE COMMERCIALISATION */

  callGetStore() {
    return httpClient().get(`${ApiModule.Global}/Almacenes`);
  },

  callGetCustomers(filter) {
    if (filter !== undefined) {
      const params = {
        filtro: filter,
      };
      return httpClient().get(`${ApiModule.Commercialization}/Expediciones/Clientes`, { params });
    }
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/Clientes`);
  },

  callGetCarrier(filter) {
    if (filter !== undefined && filter !== null) {
      const params = {
        filtro: filter,
      };
      return httpClient().get(`${ApiModule.Commercialization}/Expediciones/Transportistas`, { params });
    }
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/Transportistas`);
  },

  callGetDock() {
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/MuellesDeCarga`);
  },

  GetDockByCode() {
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/MuellesDeCarga`);
  },

  callGetArticlesExpedition(filter) {
    if (filter !== undefined) {
      const params = {
        filtro: filter,
      };
      return httpClient().get(`${ApiModule.Commercialization}/Expediciones/Articulos`, { params });
    }
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/Articulos`);
  },

  callGetVehicle(filter) {
    if (filter !== undefined) {
      const params = {
        filtro: filter,
      };
      return httpClient().get(`${ApiModule.Commercialization}/Expediciones/Vehiculos`, { params });
    }
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/Vehiculos`);
  },

  callSalesOrderLinesAvailableForExpedition(filter) {
    if (filter !== undefined) {
      const params = {
        filtro: filter,
      };
      return httpClient().get(`${ApiModule.Commercialization}/Expediciones/LineasPedidoVentaDisponiblesParaExpedicion`, { params });
    }
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/LineasPedidoVentaDisponiblesParaExpedicion`);
  },

  callReservedSerialNumbersXSalesOrderLines(idLineaPedido) {
    const params = {
      idLineaPedidoVenta: idLineaPedido,
    };
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/ReservasPalesXLineaPedidoVenta`, { params });
  },

  callInfoStatusXSerialNumberForExpedition(idArticuloNumeroSerie) {
    const params = {
      idArticuloNumeroSerie,
    };
    return httpClient().get(`${ApiModule.Commercialization}/Expediciones/EstadoArticuloNumeroSerieParaCargas`, { params });
  },

  callGetExpedition(expedicion, estadosOrdenCarga, centro) {
    const params = { Expedicion: expedicion, EstadoOrdenCarga: estadosOrdenCarga, Centro: centro };
    return this.callGetExpeditions(params);
  },

  callBorrarReservaXOrdenCarga(idArticuloNumeroSerie, idLineaOrdenCarga, idOrdenCarga) {
    const params = {
      idArticuloNumeroSerie,
      idLineaOrdenCarga,
      idOrdenCarga,
    };
    return httpClient().delete(`${ApiModule.Commercialization}/Expediciones/BorrarReservaXOrdenCarga`, { params });
  },
  /** Borrar resrva Pale sin pedido* */
  callBorrarReservaXOrdenCargaSinPedido(idArticuloNumeroSerie, idLineaOrdenCarga, idOrdenCarga) {
    const params = {
      idArticuloNumeroSerie,
      idLineaOrdenCarga,
      idOrdenCarga,
    };
    return httpClient().delete(`${ApiModule.Commercialization}/Expediciones/BorrarReservaXOrdenCargaSinPedido`, { params });
  },
  callReservarPaleXOrdenCarga(idArticuloNumeroSerie, idLineaOrdenCarga, idOrdenCarga) {
    const params = {
      idArticuloNumeroSerie,
      idLineaOrdenCarga,
      idOrdenCarga,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/ReservarPaleXOrdenCarga`, null, { params });
  },
  callReservarPaleXOrdenCargaSinPedido(idArticuloNumeroSerie, idLineaOrdenCarga, idOrdenCarga) {
    const params = {
      idArticuloNumeroSerie,
      idLineaOrdenCarga,
      idOrdenCarga,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/ReservarPaleXOrdenCargaSinPedido`, null, { params });
  },
  /* reservar y cargar lista de pales */
  callReservarYCargarPaleXOrdenCarga(lstPales, idLineaOrdenCarga, idOrdenCarga, idLineaPedidoVenta) {
    const params = {
      idLineaOrdenCarga,
      idOrdenCarga,
      idLineaPedidoVenta,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/ReservarYCargarPaleXOrdenCarga`, lstPales, { params });
  },

  CallCargarPaleXOrdenCargaSinLineaOrden(lstPales, idOrdenCarga, conPedido) {
    const params = {
      idOrdenCarga,
      conPedido,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/CargarPaleXOrdenCargaSinLineaOrdenCarga`, lstPales, { params });
  },

  callGuardarFirmasOrdenCarga(lstFirmas, idOrdenCarga) {
    const params = {
      idOrdenCarga,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/GuardarFirmasExpedicion`, lstFirmas, { params });
  },

  callObtenerFirmasOrdenCarga(idOrdenCarga) {
    const params = {
      idOrdenCarga,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/ObtenerFirmasExpedicion`, null, { params });
  },

  callSaveComplementaryDataForExpedition(datosComplementarios, idOrdenCarga) {
    const params = {
      idOrdenCarga,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/GuardarDatosComplementariosExpedicion`, datosComplementarios, { params });
  },

  callReservePaleOnSalesOrderLine(data) {
    return httpClient().post(`${ApiModule.Sales}/ReservePaleOnSalesOrderLine`, data);
  },

  callGetExpeditions(params) {
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/Expediciones`, params);
  },

  callDeleteLinesExpedition(lstObjRemoved) {
    return httpClient().delete(`${ApiModule.Commercialization}/Expediciones/EliminarLineasExpedicion`, { data: lstObjRemoved });
  },

  callDeleteExpedition(expedition) {
    return httpClient().delete(`${ApiModule.Commercialization}/Expediciones/EliminarExpedicion`, { data: expedition });
  },

  callImprimirOrdenCarga(idOrdenCarga) {
    return new Promise((resolve, reject) => {
      httpClient().get(`${ApiModule.Commercialization}/Expediciones/ImprimirOrdenDeCarga`, {
        params: {
          idOrdenCarga,
        },
      })
        .then((response) => {
          const objJson = response.data;
          resolve(objJson);
        })
        .catch((error) => {
          console.log(error);
          reject(error);
        })
        .then(() => {
          // always executed
        });
    });
  },

  callChangeEspeditionState(expedition, newState, printDocument) {
    const params = {
      nuevoEstado: newState,
      imprimirDocumentos: printDocument,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/CambiarEstadoExpedicion`, expedition, { params });
  },

  callChangeStatusPale(idArticuloNumeroSerie, idLineaOrdenCarga, idOrdenCarga, newState) {
    const params = {
      idArticuloNumeroSerie,
      idLineaOrdenCarga,
      idOrdenCarga,
      estado: newState,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/CambiarEstadoPaleXOrdenCarga`, null, { params });
  },

  callChangeTemperaturePale(idArticuloNumeroSerie, idLineaOrdenCarga, newTemperature) {
    const params = {
      idArticuloNumeroSerie,
      idLineaOrdenCarga,
      temperatura: newTemperature,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/CambiarTemperaturaPaleLineaOrdenCarga`, null, { params });
  },

  callSaveExpedition(objExpedition) {
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/Expedicion`, objExpedition);
  },

  /** *** callSaveExpedition ****** */
  callSaveExpeditionNoSales(objExpedition) {
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/ExpedicionSinPedido`, objExpedition);
  },
  callPrintDocumentExpeditionCMR(docExpedicion, idAlbaranVenta) {
    const params = {
      idAlbaranVenta,
    };
    return httpClient().post(`${ApiModule.Commercialization}/Expediciones/ImprimirDocumentoExpedicionCMR`, docExpedicion, { params });
  },

  callSaveSaleOrder(objSaleOrder) {
    return httpClient().post(`${ApiModule.Commercialization}/GestionComercial/PedidoVenta`, objSaleOrder);
  },

  callReservations(idsPedidoVenta) {
    const params = {
      idsPedidoVenta,
    };
    return httpClient().get(`${ApiModule.Commercialization}/GestionComercial/ReservasXIdsPedidoVenta`, { params });
  },

  callPriceXSalesRateXProduction(idTarifa, date, idEnvase, idProducto, idFamiliaProducto) {
    const params = {
      idTarifa,
      date,
      idEnvase,
      idProducto,
      idFamiliaProducto,
    };
    return httpClient().get(`${ApiModule.Commercialization}/GestionComercial/PriceXSalesRateXProduction`, { params });
  },

  callDeleteSaleOrder(id) {
    const params = {
      idPedidoVenta: id,
    };
    return httpClient().delete(`${ApiModule.Commercialization}/GestionComercial/EliminarPedidoVenta`, { params });
  },

  callGetUnitsMeasuredByItem(idProducto, idEnvase, idPale) {
    const params = {
      idProducto,
      idEnvase,
      idPale,
    };
    return httpClient().get(`${ApiModule.Commercialization}/GestionComercial/ObtenerUnidadesMedidaPorArticulos`, { params });
  },

  callDeleteSaleOrderLine(lstObjRemoved) {
    return httpClient().delete(`${ApiModule.Commercialization}/GestionComercial/EliminarLineasPedidoVenta`, { data: lstObjRemoved });
  },

  /* WAREHOUSE (Almacén)  */

  /** Actualiza la cantidad ubicada de una mercancia en una lista de ubicaciones */
  callUpdateQuantityLocationList(idMerchandiseListOfLocations, idLocationOfDestination, idDefaultLocation, quantity, serialNumber) {
    const params = {
      idMercanciaListaUbicaciones: idMerchandiseListOfLocations,
      idUbicacionDestino: idLocationOfDestination,
      idUbicacionPorDefecto: idDefaultLocation,
      cantidad: quantity,
      numeroSerie: serialNumber,
    };
    return httpClient().post(`${ApiModule.Warehouse}/UbicacionesCompras/ActualizarCantidadMercanciaListaUbicaciones`, null, { params });
  },

  /** Actualiza la cantidad ubicada de una mercancia en una lista de ubicaciones para recolección. */
  callUpdateQuantityPickingList(data) {
    return httpClient().post(`${ApiModule.Warehouse}/UbicacionesVentas/ActualizarCantidadMercanciaListaUbicacionesParaRecoleccion`, data);
  },

  /** Genera el albaran de venta */
  callGenerateSalesDeliveryNoteFromPickingList(data) {
    return httpClient().post(`${ApiModule.Sales}/GenerarAlbaranVentaDesdeXListaUbicacionesParaRecoleccion`, data);
  },

  /** Genera Recuento */
  callStockCount(data) {
    return httpClient().post(`${ApiModule.Warehouse}/RecuentoYRegularizacion/RecuentoExistencias`, data);
  },
  /**
   * Genera y Regulariza el recuento.
   * @param {Partial<RecuentoExistencia>[]} data
   * @returns {Promise<AxiosResponse<any>>}
   */
  callStockCountAndRegularization(data) {
    return httpClient().post(`${ApiModule.Warehouse}/RecuentoYRegularizacion/StockCountAndRegularization`, data);
  },
  /** Regulariza el recuento */
  callRegularizeCount(data) {
    return httpClient().post(`${ApiModule.Warehouse}/RecuentoYRegularizacion/RegularizarRecuento`, data);
  },
  // Borra el recuento
  callDeleteCount(data) {
    return httpClient().delete(`${ApiModule.Warehouse}/RecuentoYRegularizacion/EliminarRecuentoExistencias`, { data });
  },

  /** Existencias a fecha para recuento */
  callStockCountToDate(fecha, idArticulo, idAlmacen, idUbicacion, idfamilia) {
    const params = {
      fecha,
      idArticulo,
      idAlmacen,
      idUbicacion,
      idFamiliaArticulo: idfamilia,
    };
    return httpClient().post(`${ApiModule.Warehouse}/RecuentoYRegularizacion/ExistenciasAFechaParaRecuento`, null, { params });
  },

  callGetLogisticUnit() {
    return httpClient().post(`${ApiModule.Warehouse}/RecuentoYRegularizacion/ObtenerArticulosNumerosSerie`, null);
  },

  callNewLot(data) {
    return httpClient().post(`${ApiModule.Warehouse}/RecuentoYRegularizacion/GuardarLote`, data);
  },

  /* ALERTAS Y NOTIFICACIONES  */

  /* TODO: Considerar borrar lo relacionado con PushNotifications, en teoría ya no se están usando
  callSubscribePushNotifications(dispositivePushId, nameApplication, namePlatform, appVersion) {

    const params = {
      idDispositivo: dispositivePushId,
      aplicacion: nameApplication,
      plataforma: namePlatform,
      versionApp: appVersion,
    };
    return httpClient().get(`${ApiModule.Alerts}/SuscribirNotificacionesPush`, { params });
  },
  callUnsubscribePushNotificationsERP(
    dispositivePushId,
    nameApplication,
    namePlatform,
    appVersion,
  ) {

    const params = {
      idDispositivo: dispositivePushId,
      aplicacion: nameApplication,
      plataforma: namePlatform,
      versionApp: appVersion,
    };
    return httpClient().get(`${ApiModule.Alerts}/DesuscribirNotificacionesPush`, { params });
  },
  */

  /** ALHONDIGAS  */

  /**
   * Fetches open sales listings.
   * @param {Entorno} entornoJSON
   * @returns {Promise<AxiosResponse<ListinVenta[]>>}
   */
  callFetchOpenSalesListing(entornoJSON) {
    return httpClient().post(`${ApiModule.Alhondigas}/Subasta/GetOpenSalesListing`, entornoJSON);
  },

  /**
   * Saves a sales listin.
   * @param {PartialDeep<ListinVenta>} listinVenta
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callSaveSalesListing(listinVenta) {
    return httpClient().post(`${ApiModule.Alhondigas}/Subasta/SaveSalesListing`, listinVenta);
  },

  /**
   * Gets the status of ordered auctions goods.
   * @param {PartialDeep<ListinVenta>} objListin
   * @returns {Promise<AxiosResponse<EstadoMercancia>>}
   */
  callFetchStatusOfOrderedGoods(objListin) {
    return httpClient().post(`${ApiModule.Alhondigas}/Subasta/StatusOfOrderedGoods`, objListin);
  },

  /**
   * Validates the sales listin.
   * @param {number} idCentroSubasta
   * @param {number} idVenta
   * @param {number} idVendedor
   * @param {number} idArticulo
   * @param {number} precio
   * @param {number} idCliente
   * @param {string | null} corte
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callGetSalesListingValidation(idCentroSubasta, idVenta, idVendedor, idArticulo, precio, idCliente, corte) {
    const params = {
      idCentroSubasta,
      idVenta,
      idVendedor,
      idArticulo,
      precio,
      idCliente,
    };

    if (corte) {
      params.corte = corte;
    }

    return httpClient().get(`${ApiModule.Alhondigas}/Subasta/GetSalesListingXVenArtPreCliCor`, { params });
  },

  /**
   * Fetches get order lines
   * @param {number} idCliente
   * @param {number} idArticulo
   * @param {string} fecha
   * @param {number} idEnvase
   * @param {number} idCentro
   * @returns {Promise<AxiosResponse<LineaPedidoVenta[]>>}
   */
  callFetchGetSalesOrderLines(idCliente, idArticulo, fecha, idEnvase, idCentro) {
    const params = {
      idCliente,
      idArticulo,
      fecha,
      idEnvase,
      idCentro,
    };
    return httpClient().post(`${ApiModule.Alhondigas}/Subasta/GetSalesOrderLines`, null, { params });
  },

  /**
   * Saves a sales order line.
   * @param {PartialDeep<LineaPedidoVenta>} lineaPedidoJSON
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callSaveOrderLine(lineaPedidoJSON) {
    const params = {};
    return httpClient().post(`${ApiModule.Alhondigas}/Subasta/SaveOrderLine`, lineaPedidoJSON, { params });
  },

  /**
   * Validates if a sales listing has an authorized.
   * @param {number} idCorredor - The ID of the runner.
   * @returns {Promise<AxiosResponse<Sujeto[]>>}
   */
  callGetSalesListingAuthorized(idCorredor) {
    const params = {
      idCorredor,
    };
    return httpClient().get(`${ApiModule.Alhondigas}/Subasta/GetAuthorized`, { params });
  },

  /**
   * Changes the status of a listing.
   * @param {CambiarEstadoListines} object
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callFetchChangeStatusSalesListing(object) {
    return httpClient().post(`${ApiModule.Alhondigas}/Subasta/ChangeStatusSalesListing`, object);
  },

  /**
   * Deletes sales listing.
   * @param {number}idListin
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callDeleteSalesListing(idListin) {
    const params = { idListin };
    return httpClient().delete(`${ApiModule.Alhondigas}/Subasta/DeleteSalesListing`, { params });
  },

  /**
   * Fetches articles per item.
   * @param {object} [options]
   * @param {string} [options.filter] - The filtering value.
   * @param {string} [options.user] - The username.
   * @param {number} [options.idCompany] - The ID of the company.
   * @param {Date | string | null} [options.date] - The date.
   * @returns {Promise<AxiosResponse<Articulo[]>>}
   */
  callFetchArticlesPerItem({
    filter = '',
    user = '',
    idCompany = 0,
    date = null,
  } = {}) {
    return callExecuteSPERP('APPERPAGRO_ArticulosVentaPDA', [
      {
        NombreParametro: 'filtro',
        Valor: filter,
      },
      {
        NombreParametro: 'usuario',
        Valor: user,
      },
      {
        NombreParametro: 'idEmpresa',
        Valor: idCompany,
      },
      {
        NombreParametro: 'fecha',
        Valor: date,
      },
    ], 'Comun.ArticuloJSON');
  },

  /**
   * Fetches articles per package.
   * @param {object} [options]
   * @param {string} [options.filter] - The filtering value.
   * @param {Date | string | null} [options.date] - The date.
   * @param {number} [options.idsBusinessGroup] - The ID of business company
   * @param {number} [options.idCompany] - The ID of the company.
   * @returns {Promise<AxiosResponse<Articulo[]>>}
   */
  callFetchArticlesPackaging({
    filter = '',
    date = null,
    idsBusinessGroup = 0,
    idCompany = 0,
  } = {}) {
    return callExecuteSPERP('APPERPAGRO_BusquedaArticuloEnvase', [
      {
        NombreParametro: 'filtro',
        Valor: filter,
      },
      {
        NombreParametro: 'FechaEntorno',
        Valor: date,
      },
      {
        NombreParametro: 'Id_GrupoEmpresarial',
        Valor: idsBusinessGroup,
      },
      {
        NombreParametro: 'Id_Empresa',
        Valor: idCompany,
      },
    ], 'Comun.ArticuloJSON');
  },

  /**
   * Fetches batch per item.
   * @param {object} [options]
   * @param {string} [options.filter] - The filtering value.
   * @param {number} [options.idWarehouse] - The id of the warehouse.
   * @param {number} [options.idSale] - The ID of the sale.
   * @param {number} [options.allowSellMultipleItemsMultipleSales] - Whether to allow selling multiple items in multiple sales.
   * @param {number} [options.idArticle] - The id of the article.
   * @returns {Promise<AxiosResponse<Partida[]>>}
   */
  callFetchBatchPerItem({
    filter = '',
    idWarehouse = 0,
    idSale = 0,
    allowSellMultipleItemsMultipleSales = 0,
    idArticle = 0,
  } = {}) {
    return callExecuteSPERP('APPERPAGRO_PartidasPdtesVenta', [
      {
        NombreParametro: 'filtro',
        Valor: filter,
      },
      {
        NombreParametro: 'idAlmacen',
        Valor: idWarehouse,
      },
      {
        NombreParametro: 'idVenta',
        Valor: idSale,
      },
      {
        NombreParametro: 'PermitirVenderPartidasMultiplesVentas',
        Valor: allowSellMultipleItemsMultipleSales,
      },
      {
        NombreParametro: 'idArticulo',
        Valor: idArticle,
      },
    ], 'PartidaJSON');
  },

  /**
   * Fetches the broker per customer.
   * @param {object} [options]
   * @param {string} [options.filter] - The filtering value.
   * @param {string | null} [options.idsBusinessGroup] - The IDs of the business groups.
   * @param {string} [options.user] - The username.
   * @param {number} [options.customerId] - The ID of the customer.
   * @param {Date | string | null} [options.date] - The date.
   * @returns {Promise<AxiosResponse<Corredor[]>>}
   */
  callFetchBrokerPerClient({
    filter = '',
    idsBusinessGroup = '',
    user = '',
    customerId = 0,
    date = null,
  } = {}) {
    return callExecuteSPERP('APPERPAGRO_CorredoresXIdGrupoEmpresarialActividad', [
      {
        NombreParametro: 'filtro',
        Valor: filter,
      },
      {
        NombreParametro: 'idsGrupoEmpresarial',
        Valor: idsBusinessGroup,
      },
      {
        NombreParametro: 'usuario',
        Valor: user,
      },
      {
        NombreParametro: 'idCliente',
        Valor: customerId,
      },
      {
        NombreParametro: 'fecha',
        Valor: date,
      },
    ], 'Agro.Alhondigas.CorredorJSON');
  },

  /**
   * Fetches customers for auctions.
   * @param {object} [options]
   * @param {string} [options.filter] - The filtering value.
   * @param {string | null} [options.idsBusinessGroup] - The ids of the business groups.
   * @param {string} [options.user] - The username.
   * @param {number | null} [options.idBroker] - The id of the broker.
   * @param {number} [options.idCompany] - The id of the company.
   * @param {Date | string | null} [options.date] - The date.
   * @param {number} [options.customization] - Whether apply customization EXT069.
   * @returns {Promise<AxiosResponse<Cliente[]>>}
   */
  callFetchClientPerBrocker({
    filter = '',
    idsBusinessGroup = '',
    user = '',
    idBroker = 0,
    idCompany = 0,
    date = null,
    customization = 0,
  } = {}) {
    return callExecuteSPERP('APPERPAGRO_ClientesSubastasXIdGrupoEmpresarialActividad', [
      {
        NombreParametro: 'filtro',
        Valor: filter,
      },
      {
        NombreParametro: 'idsGrupoEmpresarial',
        Valor: idsBusinessGroup,
      },
      {
        NombreParametro: 'usuario',
        Valor: user,
      },
      {
        NombreParametro: 'idCorredor',
        Valor: idBroker,
      },
      {
        NombreParametro: 'idEmpresa',
        Valor: idCompany,
      },
      {
        NombreParametro: 'fecha',
        Valor: date,
      },
      {
        NombreParametro: 'personalizacionExt069',
        Valor: customization,
      },
    ], 'Comun.ClienteJSON');
  },

  /**
   * Fetches sales without blocking.
   * @param {object} [options]
   * @param {string} [options.filter] - The filtering value.
   * @param {number} [options.auctionCenter] - The id of the auction center.
   * @param {number} [options.idCampanya] - The id of the campanya.
   * @returns {Promise<AxiosResponse<Venta[]>>}
   */
  callFetchSalesWithoutBlocking({
    filter = '',
    auctionCenter = 0,
    idCampanya = 0,
  } = {}) {
    return callExecuteSPERP('APPERPAGRO_VentasXCentroSubastaXCampanya', [
      {
        NombreParametro: 'filtro',
        Valor: filter,
      },
      {
        NombreParametro: 'idCentroSubasta',
        Valor: auctionCenter,
      },
      {
        NombreParametro: 'idCampanya',
        Valor: idCampanya,
      },
    ], 'Agro.Alhondigas.VentaJSON');
  },

  /**
   * @param {object} [options]
   * @param {number} [options.saleId] - The id of the sale (`Venta` entity).
   * @param {number} [options.batchId] - The id of the batch (`Partida` entity).
   * @returns {Promise<AxiosResponse<ListinVenta[]>>}
   */
  callFetchListinsByBatch({
    saleId = 0,
    batchId = 0,
  } = {}) {
    return callExecuteSPERP('APPERPAGRO_PartidasEnUsoxVenta', [
      {
        NombreParametro: 'idVenta',
        Valor: saleId,
      },
      {
        NombreParametro: 'idPartida',
        Valor: batchId,
      },
    ], 'Agro.Alhondigas.ListinVentaJSON');
  },

  /** ****************************************************** */
  /** ********** RETURNABLE PACKAGES *********************** */
  /** ****************************************************** */

  /**
   * Gets balance and limit for returnable movement.
   * @param {PartialDeep<MovimientoArticRetornables>} returnableMovement
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callGetBalanceAndLimitReturnableMovement(returnableMovement) {
    const params = {};
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/GetBalanceAndLimitReturnableMovement`, returnableMovement, { params });
  },

  /**
   * Gets balance and limit for returnable order.
   * @param {PartialDeep<PedidoArticuloRetornable>} returnableOrder
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callGetBalanceAndLimitReturnableOrder(returnableOrder) {
    const params = {};
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/GetBalanceAndLimitReturnableOrder`, returnableOrder, { params });
  },

  // TODO: Not used. Delete it?
  /**
   * Saves a returnable item order.
   * @param {PartialDeep<PedidoArticuloRetornable>} returnableOrder
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callSaveReturnableItemOrder(returnableOrder) {
    const params = {};
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/SaveReturnableItemOrder`, returnableOrder, { params });
  },

  /**
   * Saves a returnable item movement.
   * @param {PartialDeep<MovimientoArticRetornables>} returnableMovement
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callSaveReturnableItemMovement(returnableMovement) {
    const params = {};
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/SaveReturnableItemMovement`, returnableMovement, { params });
  },

  /**
   * Creates a returnable item movement from order.
   * @param {PartialDeep<PedidoArticuloRetornableLinea[]>} returnableOrderLine
   * @param {Date | string | null} selectedArrivalDate
   * @param {Date | string | null} selectedDepartureDate
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callCreateReturnableItemMovementFromOrder(returnableOrderLine, selectedArrivalDate, selectedDepartureDate) {
    const params = { selectedArrivalDate, selectedDepartureDate };
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/CreateReturnableItemMovementFromOrder`, returnableOrderLine, { params });
  },

  /**
   * Change the order status.
   * @param {string} idsLineas - The IDs of order lines.
   * @param {number} estado - The status.
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callChangeOrderStatus(idsLineas, estado) {
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/ChangeOrderStatus`, null, {
      params: {
        idsLineas,
        estado,
      },
    });
  },

  /**
   * Print the order.
   * @param {number} idOrder - The ID of the order.
   * @returns {Promise<AxiosResponse<RespuestaValidacion>>}
   */
  callPrintReturnableItemOrder(idOrder) {
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/PrintReturnableItemOrder`, null, {
      params: {
        idImprimir: idOrder,
      },
    });
  },

  /**
   * Print the movement.
   * @param {number} idMovement - The ID of the movement.
   * @returns {Promise<AxiosResponse<RespuestaValidacion>>}
   */
  callPrintReturnableItemMovement(idMovement) {
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/PrintReturnableItemMovement`, null, {
      params: {
        idImprimir: idMovement,
      },
    });
  },

  /**
   * Saves movement signature.
   * @param {Imagen[]} listImg - The ID of the movement.
   * @param {number} idMovement - The ID of the movement.
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callSaveMovementSign(listImg, idMovement) {
    return httpClient().post(`${ApiModule.Commercialization}/PedidosArticulosRetornables/SaveMovementSign`, listImg, {
      params: {
        idMovimiento: idMovement,
      },
    });
  },

  /**
   * Gets movement signature.
   * @param {number} idMovement - The ID of the movement.
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callGetMovementSign(idMovement) {
    const params = {
      idMovement,
    };
    return httpClient().get(`${ApiModule.Commercialization}/PedidosArticulosRetornables/GetMovementSign`, { params });
  },

  /**
   * Delete returnable movement
   * @param {string} idMovement
   * @returns {Promise<AxiosResponse<Respuesta>>}
   */
  callDeleteReturnableMovement(idMovement) {
    const params = { idMovement };
    return httpClient().delete(`${ApiModule.Commercialization}/PedidosArticulosRetornables/DeleteReturnableMovement`, { params });
  },
};
